import { NavLink, useNavigate } from 'react-router-dom'
import { useState, useRef, useEffect } from 'react'
import {
  LayoutDashboard,
  Megaphone,
  Bot,
  Users,
  Receipt,
  PhoneCall,
  Settings,
  Search,
  ChevronsUpDown,
  Blocks,
  LogOut,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAuth } from '@/lib/auth'

interface NavItem {
  to: string
  label: string
  icon: typeof LayoutDashboard
  badge?: { value: string | number; variant: 'emerald' | 'amber' | 'rose' | 'muted' }
  pulse?: boolean
}

interface NavGroup {
  label: string
  items: NavItem[]
}

const groups: NavGroup[] = [
  {
    label: 'General',
    items: [{ to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard }],
  },
  {
    label: '💬 WhatsApp',
    items: [
      { to: '/whatsapp', label: 'Resumen general', icon: Blocks },
      {
        to: '/whatsapp/campanas',
        label: 'Campañas',
        icon: Megaphone,
        badge: { value: 4, variant: 'muted' },
      },
      {
        to: '/whatsapp/autoservicio',
        label: 'Autoservicio',
        icon: Bot,
        badge: { value: 8, variant: 'amber' },
        pulse: true,
      },
      {
        to: '/whatsapp/humana',
        label: 'Gestión humana',
        icon: Users,
        badge: { value: 3, variant: 'rose' },
      },
      {
        to: '/whatsapp/revision',
        label: 'Revisión',
        icon: Receipt,
        badge: { value: 3, variant: 'muted' },
      },
    ],
  },
  {
    label: '📞 Llamadas voz',
    items: [{ to: '/voz', label: 'Agente Camila', icon: PhoneCall }],
  },
  {
    label: 'Config',
    items: [{ to: '/settings', label: 'Settings', icon: Settings }],
  },
]

export function Sidebar() {
  const tenant = import.meta.env.VITE_TENANT_NAME || 'Point'
  const { user, signOut } = useAuth()
  const navigate = useNavigate()
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  // Cerrar menu al click fuera
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false)
      }
    }
    if (menuOpen) document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [menuOpen])

  const emailDisplay = user?.email ?? ''
  const nombreDisplay =
    (user?.user_metadata?.full_name as string | undefined) ??
    (user?.user_metadata?.name as string | undefined) ??
    emailDisplay.split('@')[0] ??
    'Usuario'
  const iniciales = nombreDisplay
    .split(/[\s.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(s => s[0]?.toUpperCase())
    .join('') || 'U'
  const rol = (user?.user_metadata?.role as string | undefined) ?? 'Agente'

  const handleLogout = async () => {
    await signOut()
    navigate('/login', { replace: true })
  }

  return (
    <aside className="w-[260px] bg-gradient-to-b from-navy-800 to-navy-900 text-white flex-shrink-0 flex flex-col sticky top-0 h-screen">
      {/* Logo */}
      <div className="px-5 py-5 border-b border-white/5">
        <div className="flex items-center gap-2.5">
          <div className="relative w-9 h-9 bg-gradient-to-br from-amber-400 to-amber-600 rounded-lg flex items-center justify-center shadow-lg shadow-amber-500/20">
            <span className="font-display text-lg font-semibold text-white">S</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-[15px] tracking-tight">SimpliaCollect</div>
            <div className="text-[10px] uppercase tracking-wider text-amber-400/80 font-medium truncate">
              {tenant}
            </div>
          </div>
          <ChevronsUpDown className="w-4 h-4 text-white/40 flex-shrink-0" />
        </div>
      </div>

      {/* Search */}
      <div className="px-4 py-3">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            type="text"
            placeholder="Buscar..."
            className="w-full bg-white/5 border border-white/10 rounded-lg pl-9 pr-12 py-2 text-sm text-white placeholder-white/40 focus:outline-none focus:border-amber-500/50 focus:bg-white/10 transition"
          />
          <kbd className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] bg-white/10 px-1.5 py-0.5 rounded text-white/50 font-mono">
            ⌘K
          </kbd>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto pb-4">
        {groups.map(group => (
          <div key={group.label}>
            <div className="px-5 py-1.5 text-[10px] uppercase tracking-widest text-white/40 font-semibold mt-4">
              {group.label}
            </div>
            {group.items.map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/whatsapp' || item.to === '/dashboard'}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-2.5 px-5 py-2 mx-2 rounded-lg text-sm transition-all',
                    isActive
                      ? 'bg-gradient-to-r from-amber-500/20 to-amber-500/5 text-white font-medium shadow-[inset_3px_0_0_#d97a1c] pl-4'
                      : 'text-white/65 hover:bg-white/5 hover:text-white'
                  )
                }
              >
                <item.icon className="w-4 h-4 flex-shrink-0" />
                <span className="flex-1 truncate">{item.label}</span>
                {item.badge && (
                  <span
                    className={cn(
                      'text-[10px] font-mono px-1.5 py-0.5 rounded flex items-center gap-1',
                      item.badge.variant === 'amber' && 'bg-amber-500/20 text-amber-300',
                      item.badge.variant === 'rose' && 'bg-rose-500/20 text-rose-300',
                      item.badge.variant === 'emerald' &&
                        'bg-emerald-500/20 text-emerald-300',
                      item.badge.variant === 'muted' && 'text-white/40'
                    )}
                  >
                    {item.pulse && <span className="pulse-live !w-1.5 !h-1.5" />}
                    {item.badge.value}
                  </span>
                )}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>

      {/* User */}
      <div className="px-3 py-3 border-t border-white/5 relative" ref={menuRef}>
        <button
          onClick={() => setMenuOpen(o => !o)}
          className="w-full flex items-center gap-2.5 p-2 rounded-lg hover:bg-white/5 cursor-pointer transition"
        >
          <div className="w-8 h-8 bg-gradient-to-br from-amber-400 to-amber-600 rounded-full flex items-center justify-center text-xs font-semibold">
            {iniciales}
          </div>
          <div className="flex-1 min-w-0 text-left">
            <div className="text-xs font-medium truncate">{nombreDisplay}</div>
            <div className="text-[10px] text-white/50 truncate">{rol} · {tenant}</div>
          </div>
          <ChevronsUpDown className="w-4 h-4 text-white/40" />
        </button>

        {menuOpen && (
          <div className="absolute bottom-full left-3 right-3 mb-2 bg-navy-800 border border-white/10 rounded-lg shadow-xl py-1 z-50">
            <div className="px-3 py-2 border-b border-white/5">
              <div className="text-xs text-white truncate">{nombreDisplay}</div>
              <div className="text-[10px] text-white/50 truncate">{emailDisplay}</div>
            </div>
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-2 px-3 py-2 text-xs text-white/80 hover:bg-white/5 hover:text-white transition text-left"
            >
              <LogOut className="w-3.5 h-3.5" />
              Cerrar sesión
            </button>
          </div>
        )}
      </div>
    </aside>
  )
}
